"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, Compass, FileText, Hammer, Layers, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface PlanningPhase {
  id: string;
  step: string;
  title: string;
  timeline: string;
  budgetImpact: string;
  summary: string;
  deliverables: string[];
}
const planningPhases: PlanningPhase[] = [{
  id: "phase-1",
  step: "Phase 01",
  title: "Architectural & Engineering Due Diligence",
  timeline: "3 to 5 Weeks",
  budgetImpact: "8% to 12% of Total Investment",
  summary: "Detailed site measuring, zoning confirmation under City of Brookhaven codes, and load-bearing structural assessments before drawings begin.",
  deliverables: ["Existing condition laser scan & CAD drawings", "DeKalb County & Brookhaven zoning setback checks", "Structural engineering review & stamp", "Preliminary trade scoping for MEP systems"]
}, {
  id: "phase-2",
  step: "Phase 02",
  title: "Structural Alterations & Permitting Timelines",
  timeline: "4 to 7 Weeks (Concurrent)",
  budgetImpact: "35% to 45% of Construction Scope",
  summary: "Framing adjustments, foundation footings, and structural steel installation coordinated through city plan review and inspection gates.",
  deliverables: ["Submission and expediting of municipal permits", "Wall removal, flush header & post installations", "HVAC rough-in and upgraded electrical panels", "City framing and mechanical rough inspections"]
}, {
  id: "phase-3",
  step: "Phase 03",
  title: "Finish Selections & Interior Appointments",
  timeline: "6 to 10 Weeks",
  budgetImpact: "45% to 55% of Project Total",
  summary: "Precision cabinet installation, natural stone slab fitting, artisanal tile miters, and plumbing fixture trim tailored to your lifestyle.",
  deliverables: ["Custom cabinetry delivery and laser level setting", "Templating and installation of quartzite or marble", "Hardwood flooring feathering and custom stains", "Final fixtures, trim out, and walkthrough punch list"]
}];
const costDrivers = [{
  title: "Neighborhood Zoning & Setbacks",
  desc: "Brookhaven historic and residential lot coverage limits directly dictate addition footprints and runoff retention."
}, {
  title: "Structural Wall Relocations",
  desc: "Converting segmented 1960s ranches to open floor plans requires recessed steel beams and foundation piering."
}, {
  title: "Cabinetry & Stone Specifications",
  desc: "Full-overlay bespoke joinery and imported natural quartzite represent the single largest variable in kitchen remodels."
}, {
  title: "Utility Infrastructure Upgrades",
  desc: "Modern luxury appliances frequently require dedicated 200-amp service panels and updated gas line feeds."
}];
export default function CostGuide() {
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = {
    duration: shouldReduceMotion ? 0 : 0.5,
    ease: [0.16, 1, 0.3, 1] as const
  };
  return <section id="cost-planning-guide" data-nav="light" className="relative bg-background text-foreground py-24 md:py-28 overflow-hidden">
      {/* Decorative architectural background line */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      <div className="container mx-auto px-5 sm:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={transitionConfig} className="flex items-center gap-2 mb-4">
            <Badge variant="outline" className="border-primary/40 bg-primary/10 text-foreground text-xs font-semibold tracking-wider uppercase px-3 py-1 rounded-full">
              <Compass className="size-3.5 text-primary mr-1 inline" />
              Transparent Estimates & Timelines
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          ...transitionConfig,
          delay: 0.08
        }} className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-[1.18] text-foreground mb-6">
            How Brookhaven renovations are planned and priced.
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          ...transitionConfig,
          delay: 0.16
        }} className="text-base sm:text-lg text-foreground/80 leading-relaxed font-sans">
            Permitting rules, structural discoveries, and custom finishes shape
            every timeline and budget. In Brookhaven, Dunwoody, and Buckhead,
            thoughtful planning during architectural review prevents costly
            surprises once framing begins. Here is how our team manages the key
            phases before and during construction.
          </motion.p>
        </div>

        {/* 12-Column Asymmetric Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: 5 Cols - Summary Guide Card */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          ...transitionConfig,
          delay: 0.1
        }} className="lg:col-span-5">
            <Card className="rounded-2xl border border-border/80 bg-card/90 shadow-md backdrop-blur-md overflow-hidden relative">
              <div className="absolute top-0 inset-x-0 h-1 bg-primary" />
              <CardHeader className="pt-8 pb-4">
                <div className="flex items-center gap-2 mb-2 text-primary font-semibold text-xs uppercase tracking-widest">
                  <ShieldCheck className="size-4" />
                  Local Building Standards
                </div>
                <CardTitle className="font-serif text-2xl font-semibold text-foreground">
                  The Four Primary Cost Drivers
                </CardTitle>
                <CardDescription className="text-foreground/70 text-sm mt-1">
                  Budget allocations vary by home style and age. We evaluate
                  these elements during your initial in-home discovery meeting.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6 pt-2 pb-8">
                <div className="space-y-4">
                  {costDrivers.map((driver, i) => <div key={driver.title} data-index={i} className="p-4 rounded-xl bg-muted/50 border border-border/60 transition-[background-color,border-color] duration-200 hover:bg-muted hover:border-primary/30">
                      <h4 className="font-semibold text-sm text-foreground flex items-center gap-2 mb-1.5">
                        <span className="size-1.5 rounded-full bg-primary shrink-0" />
                        {driver.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-foreground/75 leading-relaxed pl-3.5">
                        {driver.desc}
                      </p>
                    </div>)}
                </div>

                <Separator className="my-6 bg-border/80" />

                {/* Practical Advice Note */}
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <Sparkles className="size-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-semibold text-foreground mb-1">
                        Our Transparent Pricing Promise
                      </h5>
                      <p className="text-xs text-foreground/80 leading-relaxed">
                        Every proposal includes a line-item material breakdown,
                        permit processing allocations, and a planned
                        contingency reserve so your final price remains clear.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <Button asChild size="lg" className="w-full bg-primary text-primary-foreground font-semibold shadow-sm hover:bg-primary/90 transition-all rounded-lg">
                    <Link to="/ContactRequestAnEstimatePage">
                      Request Detailed Estimate
                      <ArrowRight className="size-4 ml-1.5" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column: 7 Cols - Structured Phase Cards */}
          <div className="lg:col-span-7 space-y-6">
            {planningPhases.map((phase, i) => <motion.div key={phase.id} data-index={i} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            ...transitionConfig,
            delay: 0.15 + i * 0.08
          }}>
                <Card className="rounded-2xl border border-border/80 bg-card hover:border-primary/50 transition-[border-color,box-shadow] duration-300 shadow-sm hover:shadow-md overflow-hidden">
                  <CardHeader className="pb-3 pt-7">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                      <span className="text-xs font-mono font-semibold uppercase tracking-wider text-primary bg-primary/10 px-2.5 py-1 rounded-md">
                        {phase.step}
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary" className="bg-muted text-foreground text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1.5">
                          <Clock className="size-3 text-primary" />
                          {phase.timeline}
                        </Badge>
                        <Badge variant="outline" className="border-border text-foreground/80 text-xs font-medium px-2.5 py-0.5 rounded-full">
                          {phase.budgetImpact}
                        </Badge>
                      </div>
                    </div>

                    <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-foreground pt-1">
                      {phase.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-foreground/75 leading-relaxed mt-2 font-sans">
                      {phase.summary}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pb-7 pt-2">
                    <div className="mt-2 pt-4 border-t border-border/60">
                      <p className="text-xs uppercase font-semibold tracking-wider text-foreground/60 mb-3 flex items-center gap-1.5">
                        <Layers className="size-3.5 text-primary" />
                        Milestone Benchmarks & Deliverables
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {phase.deliverables.map((item, dIndex) => <div key={dIndex} data-index={dIndex} className="flex items-start gap-2 text-xs sm:text-sm text-foreground/85 leading-tight">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </div>)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}

            {/* Bottom Note & Consultation Banner */}
            <motion.div initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            ...transitionConfig,
            delay: 0.4
          }} className="p-6 rounded-2xl bg-secondary text-secondary-foreground flex flex-col sm:flex-row items-center justify-between gap-5 shadow-md">
              <div className="space-y-1 text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 text-primary text-xs font-semibold uppercase tracking-wider">
                  <FileText className="size-4" />
                  Brookhaven Permitting Assistance
                </div>
                <h4 className="font-serif text-lg font-medium text-secondary-foreground">
                  Ready to map out your home's scope and budget?
                </h4>
                <p className="text-xs sm:text-sm text-secondary-foreground/80">
                  We schedule on-site walkthroughs across Brookhaven, Chamblee,
                  and Sandy Springs.
                </p>
              </div>

              <Button asChild className="bg-primary text-primary-foreground font-semibold px-6 py-2.5 rounded-lg shrink-0 shadow-sm hover:bg-primary/90 transition-all">
                <Link to="/AboutPage">Explore Our Process
                  <ArrowRight className="size-4 ml-1" /></Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}